// The one way the SPA talks to the API: same origin, JSON, the signed-in
// caller's bearer token when there is one, and every refusal read as the
// RFC 9457 problem the API sends (errors.md).
import type { ProblemDetails } from './generated/ProblemDetails';

/** A refused or failed request, carrying the API's problem. */
export class ApiError extends Error {
    readonly problem: ProblemDetails;

    constructor(problem: ProblemDetails) {
        super(problem.detail || problem.title);
        this.name = 'ApiError';
        this.problem = problem;
    }

    /** The stable error code, such as `Build.WrongState`. */
    get code(): string {
        return this.problem.errorCode;
    }

    /** The HTTP status. */
    get status(): number {
        return this.problem.status;
    }

    /** What is wrong with a field, for a validation problem. */
    fieldErrors(field: string): readonly string[] {
        return this.problem.errors?.[field] ?? [];
    }
}

type TokenSource = () => string | undefined;

let tokenSource: TokenSource = () => undefined;

/** Called once by the sign-in bridge, so every request carries the current token. */
export function setTokenSource(source: TokenSource): void {
    tokenSource = source;
}

export interface RequestOptions {
    readonly method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    readonly body?: unknown;
    readonly signal?: AbortSignal;
}

/** A request whose answer is JSON, or nothing for a 204. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const headers = new Headers({ Accept: 'application/json' });
    const token = tokenSource();
    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    }
    let body: BodyInit | undefined;
    if (options.body instanceof FormData) {
        body = options.body;
    } else if (options.body !== undefined) {
        headers.set('Content-Type', 'application/json');
        body = JSON.stringify(options.body);
    }
    const init: RequestInit = { method: options.method ?? 'GET', headers };
    if (body !== undefined) init.body = body;
    if (options.signal) init.signal = options.signal;
    const response = await fetch(path, init);
    if (!response.ok) {
        throw new ApiError(await problemOf(response));
    }
    if (response.status === 204 || response.headers.get('content-length') === '0') {
        return undefined as T;
    }
    return (await response.json()) as T;
}

async function problemOf(response: Response): Promise<ProblemDetails> {
    const fallback: ProblemDetails = {
        type: 'about:blank',
        title: response.statusText || 'Something went wrong',
        status: response.status,
        detail: response.status >= 500 ? 'The shop floor did not answer. Try again in a moment.' : response.statusText,
        instance: new URL(response.url || '/', window.location.origin).pathname,
        traceId: response.headers.get('x-request-id') ?? '',
        errorCode: `Http.${String(response.status)}`,
    };
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('json')) return fallback;
    try {
        return { ...fallback, ...((await response.json()) as Partial<ProblemDetails>) };
    } catch {
        return fallback;
    }
}

/** The person-facing message for any thrown value. */
export function messageOf(error: unknown): string {
    if (error instanceof ApiError) return error.message;
    if (error instanceof Error) return error.message;
    return 'Something went wrong.';
}
